import { useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { isAdminAccount } from 'src/lib/auth/roles';
import { Button } from 'src/lib/components/Button.component';
import { TextInput } from 'src/lib/components/TextInput.component';
import { toasting } from 'src/lib/components/Toast.component';
import { fpost } from 'src/lib/fetch';
import locale from 'src/locales/ja.json';
import { LoginReq } from '../dto/login/login.req';
import { LoginRes } from '../dto/login/login.res';

interface AdminLoginLocationState {
  pathname?: string;
}

export function AdminLoginPage() {
  const eForm = useRef<HTMLFormElement>(null);

  const eTextEmail = useRef<HTMLInputElement>(null);
  const eTextPassword = useRef<HTMLInputElement>(null);

  const eButtonSubmit = useRef<HTMLButtonElement>(null);

  const navigate = useNavigate();
  const { state } = useLocation<AdminLoginLocationState>();

  const handleButtonSubmit = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    if (!eForm.current?.checkValidity()) return eForm.current?.reportValidity();

    try {
      const res = await fpost<LoginReq, LoginRes>('api/auth/login', {
        body: {
          email: eTextEmail.current?.value || '',
          password: eTextPassword.current?.value || '',
          role: 'admin',
        },
      });

      const loginResponse = res?.[1];
      const token = loginResponse?.token;

      if (token && isAdminAccount(loginResponse)) {
        localStorage.setItem('user', JSON.stringify(loginResponse));
        const requestedPath = state?.pathname;
        const destination =
          requestedPath?.startsWith('/admin/') && !['/admin/login', '/admin/logout'].includes(requestedPath)
            ? requestedPath
            : '/admin/areas';
        navigate(destination, { replace: true });
        return;
      }

      throw new Error('This account cannot use the admin login.');
    } catch (e) {
      console.error('Error logging in:', e);
      toasting({ children: '認証に失敗しました', containerProps: { className: 'border-red-600' } });
    }
  };

  return (
    <form className="flex min-h-screen flex-col items-center justify-center bg-slate-100 px-16 py-10" ref={eForm}>
      <h1 className="basis-full text-center text-3xl">管理者ログイン</h1>

      <TextInput
        labelProps={{
          children: 'メールアドレス',
        }}
        type="email"
        containerProps={{
          className: 'mt-16 basis-full',
          style: {
            minWidth: '24rem',
            maxWidth: '48rem',
          },
        }}
        required
        ref={eTextEmail}
      />

      <TextInput
        labelProps={{
          children: 'パスワード',
        }}
        type="password"
        containerProps={{
          className: 'mt-4 basis-full',
          style: {
            minWidth: '24rem',
            maxWidth: '48rem',
          },
        }}
        required
        ref={eTextPassword}
      />

      <Button
        className="mx-auto mt-6 rounded-md bg-black text-xs text-white"
        style={{
          minWidth: '256px',
        }}
        type="submit"
        onClick={handleButtonSubmit}
        ref={eButtonSubmit}>
        ログイン
      </Button>

      <div>
        <Link
          to="/login"
          className="mt-5 flex items-center justify-center text-sm font-normal text-gray-900 dark:text-white md:justify-start md:rounded-md md:p-2 md:hover:bg-gray-100 md:dark:hover:bg-slate-500">
          <span className="ml-3 whitespace-nowrap md:inline-block">{locale['login-with-user']['ja']}</span>
        </Link>
      </div>
    </form>
  );
}
